import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Save, Smartphone, Plus, Trash2, Mail, Phone, Instagram, Facebook, MapPin, CreditCard } from 'lucide-react';

export default function SiteConfigPage() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/site-config').then(r => setConfig(r.data)).catch(() => toast.error('Erreur chargement'));
  }, []);

  const set = (k, v) => setConfig(c => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/site-config', config);
      toast.success('Configuration sauvegardée');
    } catch (e) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (!config) return <div className="p-8 text-muted-foreground">Chargement…</div>;

  const phones = config.wero_phones || [];
  const codes = config.promo_codes || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="site-config-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-display tracking-tight">Configuration du site</h1>
          <p className="text-muted-foreground text-sm mt-1">Marque, contact, paiements alternatifs</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-config-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Sauvegarde…' : 'Sauvegarder'}
        </Button>
      </div>

      <Tabs defaultValue="brand" className="w-full">
        <TabsList className="bg-card border border-border/40">
          <TabsTrigger value="brand">Marque</TabsTrigger>
          <TabsTrigger value="contact">Contact</TabsTrigger>
          <TabsTrigger value="payments">Paiements</TabsTrigger>
          <TabsTrigger value="promos">Codes Promo</TabsTrigger>
        </TabsList>

        <TabsContent value="brand" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-display">Identité</CardTitle>
              <CardDescription>Apparaît dans le header public, le footer et les confirmations.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Nom de la marque</Label>
                <Input value={config.brand_name || ''} onChange={(e) => set('brand_name', e.target.value)} placeholder="Era Lodge" data-testid="brand-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Tagline</Label>
                <Input value={config.tagline || ''} onChange={(e) => set('tagline', e.target.value)} placeholder="Une parenthèse luxe & wellness" />
              </div>
              <div className="space-y-2">
                <Label>À propos (page d'accueil)</Label>
                <Textarea rows={4} value={config.about_text || ''} onChange={(e) => set('about_text', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>URL Vidéo Hero (fallback global)</Label>
                <Input value={config.hero_video_url || ''} onChange={(e) => set('hero_video_url', e.target.value)} placeholder="https://..." />
              </div>
              <div className="space-y-2">
                <Label>URL Image Hero (fallback global)</Label>
                <Input value={config.hero_image_url || ''} onChange={(e) => set('hero_image_url', e.target.value)} placeholder="https://..." />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contact" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-display">Coordonnées</CardTitle>
              <CardDescription>Affichées dans le footer du site public.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email</Label>
                <Input value={config.contact_email || ''} onChange={(e) => set('contact_email', e.target.value)} placeholder="contact@eralodge.com" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Phone className="w-4 h-4" /> Téléphone</Label>
                <Input value={config.contact_phone || ''} onChange={(e) => set('contact_phone', e.target.value)} placeholder="+33 6 ..." />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Adresse</Label>
                <Input value={config.address || ''} onChange={(e) => set('address', e.target.value)} placeholder="Lieu de la suite" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Instagram className="w-4 h-4" /> Instagram URL</Label>
                  <Input value={config.instagram_url || ''} onChange={(e) => set('instagram_url', e.target.value)} placeholder="https://instagram.com/..." />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Facebook className="w-4 h-4" /> Facebook URL</Label>
                  <Input value={config.facebook_url || ''} onChange={(e) => set('facebook_url', e.target.value)} placeholder="https://facebook.com/..." />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2"><Smartphone className="w-5 h-5" /> Wero / Paylib</CardTitle>
              <CardDescription>Numéros affichés au client pour le paiement par téléphone.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {phones.length === 0 && <p className="text-sm text-muted-foreground">Aucun numéro configuré.</p>}
              {phones.map((p, i) => (
                <div key={i} className="flex gap-3" data-testid={`wero-phone-${i}`}>
                  <Input value={p} onChange={(e) => set('wero_phones', phones.map((x, idx) => idx === i ? e.target.value : x))} placeholder="0605641364" />
                  <Button variant="ghost" size="icon" onClick={() => set('wero_phones', phones.filter((_, idx) => idx !== i))} className="text-destructive hover:text-destructive hover:bg-destructive/10">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={() => set('wero_phones', [...phones, ''])} data-testid="add-wero-btn">
                <Plus className="w-4 h-4 mr-2" /> Ajouter un numéro
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2"><CreditCard className="w-5 h-5" /> Coordonnées bancaires</CardTitle>
              <CardDescription>Optionnel — pour proposer un virement classique.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Titulaire</Label>
                <Input value={config.bank_holder || ''} onChange={(e) => set('bank_holder', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>IBAN</Label>
                <Input value={config.bank_iban || ''} onChange={(e) => set('bank_iban', e.target.value)} placeholder="FR76 ..." />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="promos" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-display">Codes promotionnels</CardTitle>
              <CardDescription>Réduction en pourcentage appliquée au sous-total.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {codes.length === 0 && <p className="text-sm text-muted-foreground">Aucun code pour l'instant.</p>}
              {codes.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-3" data-testid={`promo-row-${i}`}>
                  <Input className="col-span-5" value={c.code || ''} onChange={(e) => set('promo_codes', codes.map((x, idx) => idx === i ? { ...x, code: e.target.value.toUpperCase() } : x))} placeholder="WELCOME10" />
                  <Input className="col-span-3" type="number" min="0" max="100" value={c.percent || 0} onChange={(e) => set('promo_codes', codes.map((x, idx) => idx === i ? { ...x, percent: parseFloat(e.target.value) || 0 } : x))} placeholder="10" />
                  <div className="col-span-3 flex items-center text-sm text-muted-foreground">% de remise</div>
                  <Button variant="ghost" size="icon" className="col-span-1 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => set('promo_codes', codes.filter((_, idx) => idx !== i))}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={() => set('promo_codes', [...codes, { code: '', percent: 10, active: true }])} data-testid="add-promo-btn">
                <Plus className="w-4 h-4 mr-2" /> Ajouter un code
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
